import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { 
  ArrowRight, 
  CheckCircle, 
  Star, 
  Users, 
  Code, 
  Palette, 
  Smartphone, 
  Globe, 
  TrendingUp, 
  BarChart3,
  ShoppingBag,
  Mail,
  Phone,
  MapPin,
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
  Youtube,
  Headphones,
  CreditCard,
  PayPal,
  MessageCircle,
  Calendar,
  User,
  Eye,
  Heart,
  Share2,
  ChevronRight,
  ChevronLeft,
  Play,
  Award,
  Zap,
  Shield,
  Clock,
  MessageSquare
} from 'lucide-react';
import { newsletterAPI } from '../services/api';

const HomeOne = () => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { formatPrice } = useCurrency();
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterSubscribed, setNewsletterSubscribed] = useState(false);
  const [newsletterLoading, setNewsletterLoading] = useState(false);

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme === 'dark') {
      setIsDarkMode(true);
      document.documentElement.classList.add('dark');
    }
  }, []);

  const handleNewsletterSubscribe = async (e) => {
    e.preventDefault();
    if (!newsletterEmail.trim()) return;

    setNewsletterLoading(true);
    try {
      const response = await newsletterAPI.subscribe({ email: newsletterEmail });
      if (response.data.success) {
        setNewsletterSubscribed(true);
        setNewsletterEmail('');
      }
    } catch (error) {
      console.error('Error subscribing to newsletter:', error);
    } finally {
      setNewsletterLoading(false);
    }
  };

  return (
    <div className={`min-h-screen ${isDarkMode ? 'dark' : ''}`}>
      {/* Hero Section */}
      <section className="py-24 px-4 bg-white dark:bg-[#0f0f0f]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Content */}
            <div className="space-y-8">
              <h1 className="text-4xl md:text-5xl lg:text-[56px] font-bold text-[#0f172a] dark:text-[#f1f5f9] leading-tight">
                Full Range Of Digital Agency Services
              </h1>
              <p className="text-lg text-[#64748b] dark:text-[#cbd5e1] max-w-lg">
                We provide comprehensive digital solutions to help your business grow and succeed in the modern digital landscape.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button className="bg-[#2563eb] dark:bg-[#3b82f6] text-white px-8 py-3.5 rounded-[20px] font-bold hover:opacity-90 transition-all duration-200 flex items-center justify-center">
                  Get Started
                  <ArrowRight className="ml-2 w-5 h-5" />
                </button>
                <button className="bg-white dark:bg-[#1e293b] text-[#2563eb] dark:text-[#3b82f6] border-2 border-[#2563eb] dark:border-[#3b82f6] px-8 py-3.5 rounded-[20px] font-bold hover:bg-[#2563eb] hover:text-white dark:hover:bg-[#3b82f6] dark:hover:text-white transition-all duration-200">
                  Contact Us
                </button>
              </div>
            </div>

            {/* Right Column - Image */}
            <div className="relative">
              <div className="w-full h-96 bg-gradient-to-br from-red-400 to-pink-500 rounded-[20px] overflow-hidden shadow-2xl">
                <div className="w-full h-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center">
                  <div className="text-white text-center">
                    <Users className="w-32 h-32 mb-4" />
                    <p className="text-xl font-semibold">Digital Agency</p>
                    <p className="text-sm opacity-90">Professional Services</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Trusted Logos */}
          <div className="mt-16 pt-8 border-t border-[#e2e8f0] dark:border-[#334155]">
            <p className="text-center text-[#64748b] dark:text-[#cbd5e1] mb-8">Trusted by leading companies</p>
            <div className="flex justify-center items-center space-x-12 opacity-60">
              {['Company A', 'Company B', 'Company C', 'Company D', 'Company E'].map((company, index) => (
                <div key={index} className="text-[#64748b] dark:text-[#cbd5e1] font-semibold">
                  {company}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-24 px-4 bg-[#f9fafb] dark:bg-[#1e293b]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-[36px] font-bold text-[#0f172a] dark:text-[#f1f5f9] mb-6">
              Best Services We Provide
            </h2>
            <p className="text-lg text-[#64748b] dark:text-[#cbd5e1] max-w-2xl mx-auto">
              We offer a comprehensive range of digital services to help your business thrive in the modern digital landscape.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Code,
                title: "Web Development",
                description: "Custom websites and web applications built with modern technologies and best practices."
              },
              {
                icon: Smartphone,
                title: "Mobile Apps",
                description: "Native and cross-platform mobile applications for iOS and Android platforms."
              },
              {
                icon: Palette,
                title: "UI/UX Design",
                description: "Beautiful and functional user interfaces that provide exceptional user experiences."
              }
            ].map((service, index) => (
              <div key={index} className="bg-white dark:bg-[#1e293b] rounded-[20px] p-8 shadow-lg hover:shadow-xl hover:-translate-y-2 transition-all duration-300">
                <div className="w-16 h-16 bg-[#facc15] rounded-full flex items-center justify-center mb-6">
                  <service.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-[#0f172a] dark:text-[#f1f5f9] mb-4">
                  {service.title}
                </h3>
                <p className="text-[#64748b] dark:text-[#cbd5e1] leading-relaxed">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section className="py-24 px-4 bg-white dark:bg-[#0f0f0f]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-[36px] font-bold text-[#0f172a] dark:text-[#f1f5f9] mb-6">
              We Do Everything To Product Design
            </h2>
            <p className="text-lg text-[#64748b] dark:text-[#cbd5e1] max-w-2xl mx-auto">
              Our comprehensive approach covers every aspect of product design and development.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              {
                icon: Palette,
                title: "UI/UX",
                color: "from-blue-400 to-blue-600"
              },
              {
                icon: TrendingUp,
                title: "Strategy",
                color: "from-green-400 to-green-600"
              },
              {
                icon: Award,
                title: "Branding",
                color: "from-purple-400 to-purple-600"
              },
              {
                icon: Smartphone,
                title: "App Design",
                color: "from-orange-400 to-orange-600"
              }
            ].map((feature, index) => (
              <div key={index} className="bg-white dark:bg-[#1e293b] rounded-[20px] p-8 shadow-lg hover:shadow-xl transition-all duration-300">
                <div className={`w-20 h-20 bg-gradient-to-br ${feature.color} rounded-full flex items-center justify-center mb-6`}>
                  <feature.icon className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-[#0f172a] dark:text-[#f1f5f9]">
                  {feature.title}
                </h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-24 px-4 bg-[#f9fafb] dark:bg-[#1e293b]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-[36px] font-bold text-[#0f172a] dark:text-[#f1f5f9] mb-6">
              Featured Products
            </h2>
            <p className="text-lg text-[#64748b] dark:text-[#cbd5e1] max-w-2xl mx-auto">
              Discover our handpicked selection of premium digital products and solutions.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                name: "Premium Web Template",
                price: formatPrice(99),
                rating: 5,
                image: "/images/product-1.jpg"
              },
              {
                name: "Mobile App Kit",
                price: formatPrice(149),
                rating: 5,
                image: "/images/product-2.jpg"
              },
              {
                name: "UI Component Library",
                price: formatPrice(79),
                rating: 5,
                image: "/images/product-3.jpg"
              },
              {
                name: "Dashboard Template",
                price: formatPrice(129),
                rating: 5,
                image: "/images/product-4.jpg"
              }
            ].map((product, index) => (
              <div key={index} className="bg-white dark:bg-[#1e293b] rounded-[20px] overflow-hidden shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300">
                <div className="h-48 bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center">
                  <div className="text-white text-center">
                    <ShoppingBag className="w-16 h-16 mb-4" />
                    <p className="text-sm">Product Image</p>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-bold text-[#0f172a] dark:text-[#f1f5f9] mb-2">
                    {product.name}
                  </h3>
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-2xl font-bold text-[#2563eb] dark:text-[#3b82f6]">
                      {product.price}
                    </span>
                    <div className="flex items-center space-x-1">
                      {[...Array(product.rating)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />
                      ))}
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button className="flex-1 bg-[#2563eb] dark:bg-[#3b82f6] text-white px-4 py-2 rounded-[20px] text-sm font-bold hover:opacity-90 transition-opacity">
                      Live Preview
                    </button>
                    <button 
                      onClick={() => {
                        // Create a mock product object for the demo
                        const mockProduct = {
                          id: `product-${index}`,
                          name: product.name,
                          price: product.price,
                          slug: `product-${index}`,
                          regular_license_price: product.price
                        };
                        addToCart(mockProduct);
                        navigate('/cart');
                      }}
                      className="flex-1 bg-[#f97316] dark:bg-[#fb923c] text-white px-4 py-2 rounded-[20px] text-sm font-bold hover:opacity-90 transition-opacity text-center"
                    >
                      Add to Cart
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 px-4 bg-white dark:bg-[#0f0f0f]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-[36px] font-bold text-[#0f172a] dark:text-[#f1f5f9] mb-6">
              Why Choose Us
            </h2>
            <p className="text-lg text-[#64748b] dark:text-[#cbd5e1] max-w-2xl mx-auto">
              We deliver exceptional results through our proven methodologies and dedicated team.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Shield,
                title: "Secure & Reliable",
                description: "Your data and projects are protected with enterprise-grade security measures."
              },
              {
                icon: Clock,
                title: "Fast Delivery",
                description: "We deliver projects on time with rapid development and efficient workflows."
              },
              {
                icon: MessageSquare,
                title: "24/7 Support",
                description: "Our dedicated support team is available around the clock to help you."
              }
            ].map((feature, index) => (
              <div key={index} className="bg-white dark:bg-[#1e293b] rounded-[20px] p-8 shadow-lg hover:shadow-xl transition-all duration-300">
                <div className="w-16 h-16 bg-[#facc15] rounded-full flex items-center justify-center mb-6">
                  <feature.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-[#0f172a] dark:text-[#f1f5f9] mb-4">
                  {feature.title}
                </h3>
                <p className="text-[#64748b] dark:text-[#cbd5e1]">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Working Process */}
      <section className="py-24 px-4 bg-[#f9fafb] dark:bg-[#1e293b]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-[36px] font-bold text-[#0f172a] dark:text-[#f1f5f9] mb-6">
              Our Working Process
            </h2>
            <p className="text-lg text-[#64748b] dark:text-[#cbd5e1] max-w-2xl mx-auto">
              We follow a systematic approach to ensure successful project delivery.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              {
                step: "01",
                title: "Idea",
                description: "We start by understanding your vision and requirements."
              },
              {
                step: "02",
                title: "Design",
                description: "Our designers create beautiful and functional interfaces."
              },
              {
                step: "03",
                title: "Development",
                description: "We build your solution with cutting-edge technologies."
              },
              {
                step: "04",
                title: "Launch",
                description: "We deploy and maintain your project for optimal performance."
              }
            ].map((process, index) => (
              <div key={index} className="text-center">
                <div className="w-20 h-20 bg-[#2563eb] dark:bg-[#3b82f6] rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl font-bold text-white">{process.step}</span>
                </div>
                <h3 className="text-xl font-bold text-[#0f172a] dark:text-[#f1f5f9] mb-4">
                  {process.title}
                </h3>
                <p className="text-[#64748b] dark:text-[#cbd5e1]">
                  {process.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Latest Projects */}
      <section className="py-24 px-4 bg-white dark:bg-[#0f0f0f]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-[36px] font-bold text-[#0f172a] dark:text-[#f1f5f9] mb-6">
              Our Latest Projects
            </h2>
            <p className="text-lg text-[#64748b] dark:text-[#cbd5e1] max-w-2xl mx-auto">
              Explore our recent work and see how we've helped businesses achieve their goals.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                title: "E-Commerce Platform",
                category: "Web App",
                image: "/images/project-1.jpg"
              },
              {
                title: "Mobile Banking App",
                category: "Mobile",
                image: "/images/project-2.jpg"
              },
              {
                title: "Dashboard Template",
                category: "Template",
                image: "/images/project-3.jpg"
              },
              {
                title: "CRM System",
                category: "SaaS",
                image: "/images/project-4.jpg"
              }
            ].map((project, index) => (
              <div key={index} className="relative group overflow-hidden rounded-[20px] shadow-lg">
                <div className="h-64 bg-gradient-to-br from-green-400 to-blue-500 flex items-center justify-center">
                  <div className="text-white text-center">
                    <Code className="w-16 h-16 mb-4" />
                    <p className="text-sm">Project Image</p>
                  </div>
                </div>
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-300 flex items-center justify-center">
                  <div className="text-white text-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <span className="inline-block bg-[#2563eb] dark:bg-[#3b82f6] px-3 py-1 rounded-full text-xs font-medium mb-2">
                      {project.category}
                    </span>
                    <h3 className="text-lg font-bold">{project.title}</h3>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Meet Our Team */}
      <section className="py-24 px-4 bg-[#f9fafb] dark:bg-[#1e293b]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-[36px] font-bold text-[#0f172a] dark:text-[#f1f5f9] mb-6">
              Meet Our Team
            </h2>
            <p className="text-lg text-[#64748b] dark:text-[#cbd5e1] max-w-2xl mx-auto">
              Our talented team of professionals is dedicated to delivering exceptional results.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                name: "John Doe",
                role: "Product Manager",
                image: "/images/team-1.svg"
              },
              {
                name: "Sarah Johnson",
                role: "UI/UX Designer",
                image: "/images/team-2.jpg"
              },
              {
                name: "Mike Chen",
                role: "Frontend Developer",
                image: "/images/team-3.jpg"
              },
              {
                name: "Emily Davis",
                role: "Backend Developer",
                image: "/images/team-4.jpg"
              }
            ].map((member, index) => (
              <div key={index} className="bg-white dark:bg-[#1e293b] rounded-[20px] p-6 text-center shadow-lg hover:shadow-xl transition-all duration-300 group">
                <div className="w-24 h-24 bg-gradient-to-br from-[#2563eb] to-[#3b82f6] rounded-full flex items-center justify-center mx-auto mb-6">
                  <Users className="w-12 h-12 text-white" />
                </div>
                <h3 className="text-lg font-bold text-[#0f172a] dark:text-[#f1f5f9] mb-2">
                  {member.name}
                </h3>
                <p className="text-[#64748b] dark:text-[#cbd5e1] mb-4">
                  {member.role}
                </p>
                <div className="flex justify-center space-x-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <Facebook className="w-5 h-5 text-[#64748b] dark:text-[#cbd5e1] hover:text-[#2563eb] dark:hover:text-[#3b82f6] cursor-pointer" />
                  <Twitter className="w-5 h-5 text-[#64748b] dark:text-[#cbd5e1] hover:text-[#2563eb] dark:hover:text-[#3b82f6] cursor-pointer" />
                  <Linkedin className="w-5 h-5 text-[#64748b] dark:text-[#cbd5e1] hover:text-[#2563eb] dark:hover:text-[#3b82f6] cursor-pointer" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Acceptable Payment Methods */}
      <section className="py-16 px-4 bg-white dark:bg-[#0f0f0f]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-[36px] font-bold text-[#0f172a] dark:text-[#f1f5f9] mb-6">
              Our Acceptable Payment Methods
            </h2>
          </div>
          <div className="flex justify-center items-center space-x-8">
            {[
              { name: "PayPal", icon: CreditCard },
              { name: "Visa", icon: CreditCard },
              { name: "MasterCard", icon: CreditCard },
              { name: "Stripe", icon: CreditCard },
              { name: "GPay", icon: CreditCard }
            ].map((method, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-[#f9fafb] dark:bg-[#1e293b] rounded-full flex items-center justify-center mb-2">
                  <method.icon className="w-8 h-8 text-[#64748b] dark:text-[#cbd5e1]" />
                </div>
                <p className="text-sm text-[#64748b] dark:text-[#cbd5e1]">{method.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 px-4 bg-[#f9fafb] dark:bg-[#1e293b]">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-[36px] font-bold text-[#0f172a] dark:text-[#f1f5f9] mb-6">
              What Our Clients Say
            </h2>
          </div>
          
          <div className="bg-white dark:bg-[#1e293b] rounded-[20px] p-8 shadow-lg">
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-[#2563eb] to-[#3b82f6] rounded-full flex items-center justify-center mx-auto mb-6">
                <Users className="w-10 h-10 text-white" />
              </div>
              <p className="text-lg text-[#64748b] dark:text-[#cbd5e1] mb-6 italic">
                "The team delivered exceptional results that exceeded our expectations. Their attention to detail and innovative solutions helped us achieve our goals faster than anticipated."
              </p>
              <h3 className="text-xl font-bold text-[#0f172a] dark:text-[#f1f5f9] mb-2">
                Sarah Johnson
              </h3>
              <p className="text-[#64748b] dark:text-[#cbd5e1] mb-4">
                CEO, TechStart
              </p>
              <div className="flex justify-center space-x-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Plans */}
      <section className="py-24 px-4 bg-white dark:bg-[#0f0f0f]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-[36px] font-bold text-[#0f172a] dark:text-[#f1f5f9] mb-6">
              Pricing Plans
            </h2>
            <p className="text-lg text-[#64748b] dark:text-[#cbd5e1] max-w-2xl mx-auto mb-8">
              Choose the perfect plan for your business needs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                name: "Basic",
                price: formatPrice(29),
                features: [
                  "5 Projects",
                  "10GB Storage",
                  "Basic Support",
                  "Email Integration"
                ],
                popular: false
              },
              {
                name: "Standard",
                price: formatPrice(79),
                features: [
                  "Unlimited Projects",
                  "100GB Storage",
                  "Priority Support",
                  "Advanced Analytics",
                  "Custom Domain",
                  "API Access"
                ],
                popular: true
              },
              {
                name: "Premium",
                price: formatPrice(149),
                features: [
                  "Everything in Standard",
                  "White-label Solution",
                  "Dedicated Support",
                  "Custom Integrations",
                  "Advanced Security",
                  "SLA Guarantee"
                ],
                popular: false
              }
            ].map((plan, index) => (
              <div key={index} className={`bg-white dark:bg-[#1e293b] rounded-[20px] p-8 shadow-lg relative ${
                plan.popular ? 'ring-2 ring-[#2563eb] dark:ring-[#3b82f6]' : ''
              }`}>
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-[#2563eb] dark:bg-[#3b82f6] text-white px-4 py-1 rounded-full text-sm font-bold">
                      Most Popular
                    </span>
                  </div>
                )}
                <h3 className="text-2xl font-bold text-[#0f172a] dark:text-[#f1f5f9] mb-4">
                  {plan.name}
                </h3>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-[#2563eb] dark:text-[#3b82f6]">
                    {plan.price}
                  </span>
                  <span className="text-[#64748b] dark:text-[#cbd5e1]">/month</span>
                </div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span className="text-[#64748b] dark:text-[#cbd5e1]">{feature}</span>
                    </li>
                  ))}
                </ul>
                <button className={`w-full py-3 px-6 rounded-[20px] font-bold transition-all duration-200 ${
                  plan.popular
                    ? 'bg-[#2563eb] dark:bg-[#3b82f6] text-white hover:opacity-90'
                    : 'bg-[#f97316] dark:bg-[#fb923c] text-white hover:opacity-90'
                }`}>
                  Get Started
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter & Support */}
      <section className="py-24 px-4 bg-white dark:bg-[#0f0f0f]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Newsletter */}
            <div className="text-center lg:text-left">
              <h2 className="text-3xl md:text-[36px] font-bold text-[#0f172a] dark:text-[#f1f5f9] mb-6">
                Subscribe to Our Newsletter
              </h2>
              <p className="text-lg text-[#64748b] dark:text-[#cbd5e1] mb-8">
                Stay updated with our latest news and offers.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto lg:mx-0">
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="flex-1 px-4 py-3 border border-[#e2e8f0] dark:border-[#334155] rounded-[20px] bg-white dark:bg-[#1e293b] text-[#0f172a] dark:text-[#f1f5f9] focus:outline-none focus:ring-2 focus:ring-[#2563eb] dark:focus:ring-[#3b82f6]"
                />
                <button className="bg-[#2563eb] dark:bg-[#3b82f6] text-white px-6 py-3 rounded-[20px] font-bold hover:opacity-90 transition-opacity">
                  Subscribe Now
                </button>
              </div>
            </div>

            {/* Support */}
            <div className="bg-[#f9fafb] dark:bg-[#1e293b] rounded-[20px] p-8 text-center">
              <div className="w-16 h-16 bg-[#facc15] rounded-full flex items-center justify-center mx-auto mb-6">
                <Headphones className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-[#0f172a] dark:text-[#f1f5f9] mb-4">
                24/7 Live Chat Support
              </h3>
              <p className="text-[#64748b] dark:text-[#cbd5e1] mb-6">
                Our support team is available around the clock to help you with any questions or issues.
              </p>
              <button className="bg-[#f97316] dark:bg-[#fb923c] text-white px-6 py-3 rounded-[20px] font-bold hover:opacity-90 transition-opacity">
                Start Chat
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0f172a] dark:bg-[#0f0f0f] text-white py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {/* About Brand */}
            <div>
              <h3 className="text-xl font-bold mb-4">About Brand</h3>
              <p className="text-[#cbd5e1] mb-4">
                We are a leading digital agency dedicated to helping businesses grow and succeed in the digital landscape.
              </p>
              <div className="flex space-x-4">
                <Facebook className="w-5 h-5 text-[#cbd5e1] hover:text-white cursor-pointer" />
                <Twitter className="w-5 h-5 text-[#cbd5e1] hover:text-white cursor-pointer" />
                <Instagram className="w-5 h-5 text-[#cbd5e1] hover:text-white cursor-pointer" />
                <Linkedin className="w-5 h-5 text-[#cbd5e1] hover:text-white cursor-pointer" />
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-xl font-bold mb-4">Quick Links</h3>
              <ul className="space-y-2">
                <li><a href="#" className="text-[#cbd5e1] hover:text-white transition-colors">Home</a></li>
                <li><a href="#" className="text-[#cbd5e1] hover:text-white transition-colors">About</a></li>
                <li><a href="#" className="text-[#cbd5e1] hover:text-white transition-colors">Services</a></li>
                <li><a href="#" className="text-[#cbd5e1] hover:text-white transition-colors">Contact</a></li>
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h3 className="text-xl font-bold mb-4">Contact Info</h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <MapPin className="w-5 h-5 text-[#cbd5e1]" />
                  <span className="text-[#cbd5e1]">123 Business St, City, Country</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Phone className="w-5 h-5 text-[#cbd5e1]" />
                  <span className="text-[#cbd5e1]">+1 (555) 123-4567</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Mail className="w-5 h-5 text-[#cbd5e1]" />
                  <span className="text-[#cbd5e1]">info@company.com</span>
                </div>
              </div>
            </div>

            {/* Newsletter */}
            <div>
              <h3 className="text-xl font-bold mb-4">Newsletter</h3>
              <p className="text-[#cbd5e1] mb-4">
                Subscribe to our newsletter for updates.
              </p>
              {!newsletterSubscribed ? (
                <form onSubmit={handleNewsletterSubscribe} className="flex space-x-2">
                  <input
                    type="email"
                    placeholder="Email"
                    value={newsletterEmail}
                    onChange={(e) => setNewsletterEmail(e.target.value)}
                    className="flex-1 px-3 py-2 bg-[#1e293b] border border-[#334155] rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#3b82f6]"
                    required
                  />
                  <button 
                    type="submit"
                    disabled={newsletterLoading}
                    className="bg-[#3b82f6] text-white px-4 py-2 rounded-lg text-sm font-bold hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {newsletterLoading ? '...' : 'Subscribe'}
                  </button>
                </form>
              ) : (
                <div className="text-center">
                  <CheckCircle className="w-6 h-6 text-green-500 mx-auto mb-2" />
                  <p className="text-green-400 text-sm font-semibold">Subscribed!</p>
                </div>
              )}
            </div>
          </div>

          {/* Bottom Row */}
          <div className="border-t border-[#334155] pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-[#cbd5e1] text-sm">
              © 2024 Company Name. All rights reserved.
            </p>
            <div className="flex items-center space-x-4 mt-4 md:mt-0">
              <select className="bg-[#1e293b] border border-[#334155] rounded-lg px-3 py-2 text-sm text-white">
                <option>English</option>
                <option>Spanish</option>
                <option>French</option>
              </select>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomeOne; 